import React from 'react';

/* ====================================================================
   ANALYTICS PANEL — fancy dashboard with graphs
   ==================================================================== */
function AnalyticsPanel({ leaderboard, matches, selectedComp, setActiveStep, refreshComp }) {
  const standings = (leaderboard && leaderboard.standings) || [];
  const completedMatches = matches.filter(m => m.completed && m.result !== 'bye');

  // --- Stats ---
  const totalShots = standings.reduce((a, s) => a + (s.pointsFor || 0), 0);
  const totalEnds = standings.reduce((a, s) => a + (s.endsWon || 0), 0);
  const avgShotsPerTeam = standings.length ? (totalShots / standings.length).toFixed(1) : 0;
  const highestScorer = standings.length ? standings.reduce((best, s) => s.pointsFor > best.pointsFor ? s : best, standings[0]) : null;
  const bestWinRate = standings.length ? standings.reduce((best, s) => {
    const wr = (s.played - (s.byes || 0)) > 0 ? (s.wins / (s.played - (s.byes || 0))) * 100 : 0;
    const bwr = (best.played - (best.byes || 0)) > 0 ? (best.wins / (best.played - (best.byes || 0))) * 100 : 0;
    return wr > bwr ? s : best;
  }, standings[0]) : null;
  const biggestWinMatch = completedMatches.length ? completedMatches.reduce((best, m) => {
    const diff = Math.abs((m.homeScore || 0) - (m.awayScore || 0));
    const bestDiff = Math.abs((best.homeScore || 0) - (best.awayScore || 0));
    return diff > bestDiff ? m : best;
  }, completedMatches[0]) : null;
  const biggestWinMargin = biggestWinMatch ? Math.abs((biggestWinMatch.homeScore || 0) - (biggestWinMatch.awayScore || 0)) : 0;
  const closestMatch = completedMatches.length ? completedMatches.reduce((best, m) => {
    const diff = Math.abs((m.homeScore || 0) - (m.awayScore || 0));
    const bestDiff = Math.abs((best.homeScore || 0) - (best.awayScore || 0));
    return diff < bestDiff ? m : best;
  }, completedMatches[0]) : null;

  // --- Bar chart data: game points ---
  const maxGP = standings.length ? Math.max(...standings.map(s => s.gamePoints || 0), 1) : 1;

  // --- Points For vs Against per team ---
  const maxPF = standings.length ? Math.max(...standings.map(s => Math.max(s.pointsFor || 0, s.pointsAgainst || 0)), 1) : 1;

  // --- Win/Draw/Loss distribution pie (aggregate) ---
  const totalWins = standings.reduce((a, s) => a + (s.wins || 0), 0);
  const totalDraws = standings.reduce((a, s) => a + (s.draws || 0), 0);
  const totalLosses = standings.reduce((a, s) => a + (s.losses || 0), 0);
  const totalResults = totalWins + totalDraws + totalLosses || 1;
  const winPct = ((totalWins / totalResults) * 100).toFixed(1);
  const drawPct = ((totalDraws / totalResults) * 100).toFixed(1);
  const lossPct = ((totalLosses / totalResults) * 100).toFixed(1);

  // --- Round-by-round average scores ---
  const rounds = {};
  completedMatches.forEach(m => {
    const r = m.round || 1;
    if (!rounds[r]) rounds[r] = { total: 0, count: 0 };
    rounds[r].total += (m.homeScore || 0) + (m.awayScore || 0);
    rounds[r].count += 2; // two teams per match
  });
  const roundNums = Object.keys(rounds).map(Number).sort((a, b) => a - b);
  const roundAvgs = roundNums.map(r => (rounds[r].total / rounds[r].count).toFixed(1));
  const maxRoundAvg = roundAvgs.length ? Math.max(...roundAvgs.map(Number), 1) : 1;

  // --- Points differential chart ---
  const sortedByDiff = [...standings].sort((a, b) => (b.pointsDiff || 0) - (a.pointsDiff || 0));
  const maxAbsDiff = standings.length ? Math.max(...standings.map(s => Math.abs(s.pointsDiff || 0)), 1) : 1;

  return (
    <div className="w3-card w3-round w3-white w3-margin-top">
      <div className="w3-container w3-padding">
        <div className="analytics-header-row">
          <h4 style={{ margin: 0 }}><i className="fa fa-bar-chart w3-text-theme" style={{ marginRight: 8 }}></i>Competition Analytics</h4>
          {standings.length > 0 && (
          <div className="analytics-stats-grid" style={{ marginTop: 0, flex: 1 }}>
            <div className="analytics-stat-card w3-pale-green">
              <i className="fa fa-futbol-o analytics-stat-icon w3-text-green"></i>
              <div className="analytics-stat-value">{completedMatches.length}</div>
              <div className="analytics-stat-label">Matches Played</div>
            </div>
            <div className="analytics-stat-card w3-pale-blue">
              <i className="fa fa-bullseye analytics-stat-icon w3-text-blue"></i>
              <div className="analytics-stat-value">{totalShots}</div>
              <div className="analytics-stat-label">Total Shots Scored</div>
            </div>
            <div className="analytics-stat-card w3-pale-yellow">
              <i className="fa fa-line-chart analytics-stat-icon w3-text-orange"></i>
              <div className="analytics-stat-value">{avgShotsPerTeam}</div>
              <div className="analytics-stat-label">Avg Shots / Team</div>
            </div>
            <div className="analytics-stat-card w3-pale-red">
              <i className="fa fa-bolt analytics-stat-icon w3-text-red"></i>
              <div className="analytics-stat-value">{biggestWinMargin}</div>
              <div className="analytics-stat-label">Biggest Win Margin</div>
            </div>
          </div>
          )}
          <button className="w3-button w3-small w3-theme-l4 w3-round" style={{ whiteSpace: 'nowrap' }} onClick={() => setActiveStep('leaderboard')}>
            <i className="fa fa-arrow-left w3-margin-right"></i>Back to Results
          </button>
        </div>

        {standings.length === 0 ? (
          <p className="w3-text-grey w3-center"><em>No data yet. Score some matches to see analytics.</em></p>
        ) : (<>

        {/* === NOTABLE PERFORMANCES === */}
        <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
          <h5 style={{ marginTop: 0 }}><i className="fa fa-star w3-text-amber" style={{ marginRight: 8 }}></i>Notable Performances</h5>
          <div className="analytics-notable-grid">
            {highestScorer && (
              <div className="analytics-notable-item">
                <div className="analytics-notable-icon w3-pale-green"><i className="fa fa-bullseye w3-text-green"></i></div>
                <div>
                  <div className="analytics-notable-title">Top Scorer</div>
                  <div className="analytics-notable-name">{highestScorer.teamName}</div>
                  <div className="analytics-notable-detail">{highestScorer.pointsFor} shots scored</div>
                </div>
              </div>
            )}
            {bestWinRate && (
              <div className="analytics-notable-item">
                <div className="analytics-notable-icon w3-pale-blue"><i className="fa fa-trophy w3-text-blue"></i></div>
                <div>
                  <div className="analytics-notable-title">Best Win Rate</div>
                  <div className="analytics-notable-name">{bestWinRate.teamName}</div>
                  <div className="analytics-notable-detail">{((bestWinRate.played - (bestWinRate.byes || 0)) > 0 ? (bestWinRate.wins / (bestWinRate.played - (bestWinRate.byes || 0)) * 100).toFixed(0) : 0)}% ({bestWinRate.wins}W / {bestWinRate.played - (bestWinRate.byes || 0)}P)</div>
                </div>
              </div>
            )}
            {biggestWinMatch && (
              <div className="analytics-notable-item">
                <div className="analytics-notable-icon w3-pale-red"><i className="fa fa-bolt w3-text-red"></i></div>
                <div>
                  <div className="analytics-notable-title">Biggest Victory</div>
                  <div className="analytics-notable-name">
                    {biggestWinMatch.HomeTeam ? biggestWinMatch.HomeTeam.name : 'Team'} vs {biggestWinMatch.AwayTeam ? biggestWinMatch.AwayTeam.name : 'Team'}
                  </div>
                  <div className="analytics-notable-detail">{biggestWinMatch.homeScore} – {biggestWinMatch.awayScore} (margin: {biggestWinMargin})</div>
                </div>
              </div>
            )}
            {closestMatch && (
              <div className="analytics-notable-item">
                <div className="analytics-notable-icon w3-pale-yellow"><i className="fa fa-handshake-o w3-text-orange"></i></div>
                <div>
                  <div className="analytics-notable-title">Closest Match</div>
                  <div className="analytics-notable-name">
                    {closestMatch.HomeTeam ? closestMatch.HomeTeam.name : 'Team'} vs {closestMatch.AwayTeam ? closestMatch.AwayTeam.name : 'Team'}
                  </div>
                  <div className="analytics-notable-detail">{closestMatch.homeScore} – {closestMatch.awayScore} (margin: {Math.abs((closestMatch.homeScore || 0) - (closestMatch.awayScore || 0))})</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* === ROW 2: Game Points + Avg Score per Round === */}
        <div className="analytics-dual-grid">
        <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
          <h5 style={{ marginTop: 0 }}><i className="fa fa-bar-chart w3-text-theme" style={{ marginRight: 8 }}></i>Game Points by Team</h5>
          <div className="analytics-chart-container">
            {standings.map((s, i) => (
              <div key={s.teamId} className="analytics-bar-row">
                <div className="analytics-bar-label" title={s.teamName}>{s.teamName}</div>
                <div className="analytics-bar-track">
                  <div className="analytics-bar-fill" style={{
                    width: `${((s.gamePoints || 0) / maxGP) * 100}%`,
                    background: i === 0 ? '#4CAF50' : i === 1 ? '#66BB6A' : i === 2 ? '#81C784' : '#90CAF9',
                  }}>
                    <span className="analytics-bar-value">{s.gamePoints}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
          <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
            <h5 style={{ marginTop: 0 }}><i className="fa fa-exchange w3-text-theme" style={{ marginRight: 8 }}></i>Shots For vs Against</h5>
            <div className="analytics-chart-container">
              {standings.map((s) => (
                <div key={s.teamId} className="analytics-dual-bar-row">
                  <div className="analytics-bar-label" title={s.teamName}>{s.teamName}</div>
                  <div className="analytics-dual-bar-track">
                    <div className="analytics-bar-fill" style={{
                      width: `${((s.pointsFor || 0) / maxPF) * 100}%`,
                      background: '#4CAF50',
                      borderRadius: '4px 0 0 4px',
                    }}>
                      <span className="analytics-bar-value">{s.pointsFor}</span>
                    </div>
                    <div className="analytics-bar-fill analytics-bar-against" style={{
                      width: `${((s.pointsAgainst || 0) / maxPF) * 100}%`,
                      background: '#F44336',
                      borderRadius: '0 4px 4px 0',
                    }}>
                      <span className="analytics-bar-value">{s.pointsAgainst}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
              <span className="w3-tiny"><span className="analytics-pie-dot" style={{ background: '#4CAF50' }}></span> Shots For</span>
              <span className="w3-tiny"><span className="analytics-pie-dot" style={{ background: '#F44336' }}></span> Shots Against</span>
            </div>
          </div>
        </div>{/* end dual-grid row 2 */}

        {/* === ROW 3: Avg Score per Round + Points Differential + Ends Won === */}
        <div className="analytics-triple-grid">
        {roundNums.length > 1 && (
        <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
          <h5 style={{ marginTop: 0 }}><i className="fa fa-area-chart w3-text-theme" style={{ marginRight: 8 }}></i>Avg Score per Round</h5>
          <div className="analytics-round-chart">
            {roundNums.map((r, i) => (
              <div key={r} className="analytics-round-bar-col">
                <div className="analytics-round-bar-value">{roundAvgs[i]}</div>
                <div className="analytics-round-bar-track">
                  <div className="analytics-round-bar-fill" style={{
                    height: `${(parseFloat(roundAvgs[i]) / maxRoundAvg) * 100}%`,
                    background: `hsl(${200 + i * 20}, 70%, 55%)`,
                  }}></div>
                </div>
                <div className="analytics-round-bar-label">R{r}</div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* === POINTS DIFFERENTIAL === */}
        <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
          <h5 style={{ marginTop: 0 }}><i className="fa fa-arrows-h w3-text-theme" style={{ marginRight: 8 }}></i>Points Differential</h5>
          <div className="analytics-chart-container">
            {sortedByDiff.map((s) => {
              const pct = ((s.pointsDiff || 0) / maxAbsDiff) * 50;
              const isPositive = (s.pointsDiff || 0) >= 0;
              return (
                <div key={s.teamId} className="analytics-diff-row">
                  <div className="analytics-bar-label" title={s.teamName}>{s.teamName}</div>
                  <div className="analytics-diff-track">
                    <div className="analytics-diff-center"></div>
                    {isPositive ? (
                      <div className="analytics-diff-fill analytics-diff-positive" style={{
                        left: '50%',
                        width: `${pct}%`,
                      }}>
                        <span className="analytics-diff-value">+{s.pointsDiff}</span>
                      </div>
                    ) : (
                      <div className="analytics-diff-fill analytics-diff-negative" style={{
                        right: '50%',
                        width: `${Math.abs(pct)}%`,
                      }}>
                        <span className="analytics-diff-value">{s.pointsDiff}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* === ENDS WON CHART === */}
        {standings.some(s => (s.endsWon || 0) > 0) && (() => {
          const maxEnds = Math.max(...standings.map(s => s.endsWon || 0), 1);
          return (
            <div className="w3-card w3-round w3-light-grey w3-margin-top" style={{ padding: '16px' }}>
              <h5 style={{ marginTop: 0 }}><i className="fa fa-flag-checkered w3-text-theme" style={{ marginRight: 8 }}></i>Ends Won</h5>
              <div className="analytics-chart-container">
                {[...standings].sort((a, b) => (b.endsWon || 0) - (a.endsWon || 0)).map((s, i) => (
                  <div key={s.teamId} className="analytics-bar-row">
                    <div className="analytics-bar-label" title={s.teamName}>{s.teamName}</div>
                    <div className="analytics-bar-track">
                      <div className="analytics-bar-fill" style={{
                        width: `${((s.endsWon || 0) / maxEnds) * 100}%`,
                        background: `hsl(${260 + i * 15}, 60%, 60%)`,
                      }}>
                        <span className="analytics-bar-value">{s.endsWon}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        </div>{/* end triple-grid row 3 */}

        </>)}
      </div>
    </div>
  );
}

export default AnalyticsPanel;
